/**
 * Extracts a user-friendly error message from API error responses
 * Handles Pydantic validation errors, string errors, and generic error objects
 * 
 * @param {Error} error - The error object from axios or other source
 * @param {string} defaultMessage - Fallback message if no error detail is found
 * @returns {string} - A user-friendly error message
 */
export const extractErrorMessage = (error, defaultMessage = 'An error occurred. Please try again.') => {
  // Check for error detail in response
  const errorDetail = error.response?.data?.detail || error.detail || error.message;
  
  // Handle Pydantic validation errors (array format)
  if (Array.isArray(errorDetail) && errorDetail.length > 0) {
    return errorDetail
      .map(e => {
        // Extract the message from each validation error
        const msg = e.msg || e.message;
        const field = e.loc ? e.loc[e.loc.length - 1] : null;
        
        // If we have a field name, include it in the message
        if (field && msg) {
          return `${field}: ${msg}`;
        }
        
        return msg || String(e);
      })
      .join('; ');
  }
  
  // Handle string errors
  if (typeof errorDetail === 'string') {
    return errorDetail;
  }
  
  // Handle object errors
  if (errorDetail && typeof errorDetail === 'object') {
    return errorDetail.message || errorDetail.msg || JSON.stringify(errorDetail);
  }
  
  // Return default message
  return defaultMessage;
};

/**
 * Formats validation errors for display
 * @param {Array} validationErrors - Array of Pydantic validation errors
 * @returns {string} - Formatted error message
 */
export const formatValidationErrors = (validationErrors) => {
  if (!Array.isArray(validationErrors) || validationErrors.length === 0) {
    return '';
  }
  
  return validationErrors
    .map(error => {
      const field = error.loc ? error.loc.slice(1).join('.') : 'unknown';
      const message = error.msg || error.message || 'Invalid value';
      return `${field}: ${message}`;
    })
    .join('\n');
};

